import type {
  DispositionCode,
  DispositionCodesResponse,
  DispositionCodesUpdateResponse,
  TenantSettings,
  TenantSettingsResponse,
  ZohoOauthCredentials,
} from '@kunzite/shared-types';

import { ApiError, type TokenProvider } from './calls-api';
import { refreshOn401 } from './refresh-helper';

export class SettingsConflictError extends Error {
  override readonly name = 'SettingsConflictError';
  constructor(
    public readonly currentVersion: number,
    public readonly currentSettings: TenantSettings,
  ) {
    super('settings stale_version');
  }
}

export interface TenantsApiDeps {
  baseUrl: string;
  tokenProvider: TokenProvider;
  onJwtUpdate?: (jwt: string, fresh: { refreshExpiresAt: string; zohoOauth: ZohoOauthCredentials; expiresAt: string }) => void;
  fetch?: typeof globalThis.fetch;
}

export class TenantsApiClient {
  constructor(private readonly deps: TenantsApiDeps) {}

  getDispositionCodes(): Promise<DispositionCodesResponse> {
    return this.send<DispositionCodesResponse>('GET', '/v1/tenants/me/settings/disposition-codes');
  }

  updateDispositionCodes(codes: DispositionCode[]): Promise<DispositionCodesUpdateResponse> {
    return this.send<DispositionCodesUpdateResponse>('PUT', '/v1/tenants/me/settings/disposition-codes', { codes });
  }

  getSettings(): Promise<TenantSettingsResponse> {
    return this.send<TenantSettingsResponse>('GET', '/v1/tenants/me/settings');
  }

  async updateSettings(version: number, settings: TenantSettings): Promise<TenantSettingsResponse> {
    try {
      return await this.send<TenantSettingsResponse>('PUT', '/v1/tenants/me/settings', { version, settings });
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        const body = err.body as { current?: TenantSettingsResponse } | undefined;
        if (body?.current) {
          throw new SettingsConflictError(body.current.version, body.current.settings);
        }
      }
      throw err;
    }
  }

  private async send<T>(method: 'GET' | 'PUT', path: string, body?: unknown): Promise<T> {
    const fetcher = this.deps.fetch ?? globalThis.fetch;
    const tok = await this.deps.tokenProvider();
    return refreshOn401<T>(
      {
        baseUrl: this.deps.baseUrl,
        fetch: fetcher,
        ...(this.deps.onJwtUpdate ? { onJwtUpdate: this.deps.onJwtUpdate } : {}),
      },
      (jwt) => fetcher(`${this.deps.baseUrl}${path}`, this.init(method, jwt, body)),
      (res) => this.parse<T>(res),
      tok.jwt,
    );
  }

  private init(method: 'GET' | 'PUT', jwt: string, body?: unknown): RequestInit {
    const init: RequestInit = {
      method,
      headers: {
        Authorization: `Bearer ${jwt}`,
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      },
    };
    if (body !== undefined) init.body = JSON.stringify(body);
    return init;
  }

  private async parse<T>(res: Response): Promise<T> {
    const text = await res.text();
    const parsed: unknown = text ? JSON.parse(text) : undefined;
    if (res.status >= 200 && res.status < 300) return parsed as T;
    throw new ApiError(res.status, parsed);
  }
}
